<script setup>
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-361: one question to the engine, as a section of the checklist item
 * drawer (the one-list page). It was the separate question drawer of RED-386;
 * the drawer now shows the item (the rule) and every question that checks it
 * (the work order) in one panel. Edits the shared draft directly; the editor
 * restores its snapshot when the drawer closes without `Valmis`.
 *
 * The standards index switch follows the server's rule: a question that
 * covers only KINNITAJA TAVA items (by block, RED-361) cannot use it, so
 * ticking those items alone turns it off and locks it. The sentence REdata adds after the text comes from the
 * preview endpoint (the engine's own suffix), never from this file.
 */
const props = defineProps({
  queryKey: { type: String, required: true },
  // The item whose drawer shows this section; the warning names the others.
  itemNumber: { type: Number, default: null },
})

const { t } = useI18n()
const { draft, preview, problemsFor, tip, numberList } = inject('checklistDraft')

const editingText = ref(false)
const switchedOff = ref(false)
const pickerOpen = ref(false)

const query = computed(() => draft.queries.find(row => row.key === props.queryKey) ?? null)

const itemByNumber = number => draft.items.find(item => item.number === number) ?? null

const blocks = computed(() => [
  { key: 'standard', label: t('messages.admin.report_qc.checklist.block_standard'), items: draft.items.filter(item => item.block === 'standard') },
  { key: 'praktika', label: t('messages.admin.report_qc.checklist.block_praktika'), items: draft.items.filter(item => item.block !== 'standard') },
])

// The server's `ReportCheckItem::practiceOnly()` on the draft's own blocks.
const practiceOnly = computed(() => (query.value?.covers_items.length ?? 0) > 0 && query.value.covers_items.every(number => itemByNumber(number)?.block === 'praktika'))

const suffix = computed(() => preview.value?.questions?.find(row => row.key === props.queryKey)?.suffix ?? null)

const others = computed(() => (query.value?.covers_items ?? []).filter(number => number !== props.itemNumber))

const sharedWarning = computed(() => {
  if (props.itemNumber === null || others.value.length === 0) {
    return null
  }

  if (others.value.length === 1) {
    return t('messages.admin.report_qc.checklist.shared_warning', { n: others.value[0], title: itemByNumber(others.value[0])?.title ?? '' })
  }

  return t('messages.admin.report_qc.checklist.shared_warning_many', { n: numberList(others.value) })
})

const errorOf = field => problemsFor('query', props.queryKey).find(problem => problem.field.endsWith(`.${field}`))?.message

// The guard flip: practice-only coverage turns the index off.
watch(practiceOnly, only => {
  if (only && query.value?.uses_standards_index) {
    query.value.uses_standards_index = false
    switchedOff.value = true
  }

  if (!only) {
    switchedOff.value = false
  }
})

const covers = number => query.value.covers_items.includes(number)

const setCovers = (number, checked) => {
  const rest = query.value.covers_items.filter(n => n !== number)
  query.value.covers_items = checked ? [...rest, number].sort((a, b) => a - b) : rest
}

const inputId = name => `rqc-query-${name}-${props.queryKey}`
</script>

<template>
  <section v-if="query" class="flex flex-col gap-5 border-t border-gray-200 pt-5" :data-testid="`checklist-query-section-${query.key}`">
    <h3 class="flex items-center gap-2 text-base font-medium">
      {{ t('messages.admin.report_qc.checklist.section_query') }}
      <span class="font-mono text-sm text-gray-500">{{ query.key }}</span>
      <i
        class="pi pi-info-circle text-sm text-gray-400"
        tabindex="0"
        v-tooltip.top="tip(t('messages.admin.report_qc.checklist.section_query_tip'))"
        :aria-label="t('messages.admin.report_qc.checklist.section_query_tip')"
        data-testid="checklist-query-section-info"
      />
    </h3>

    <div class="flex items-center gap-3">
      <ToggleSwitch v-model="query.active" :input-id="inputId('active')" data-testid="checklist-query-active" />
      <label :for="inputId('active')" class="text-sm font-medium">{{ t('messages.admin.report_qc.checklist.query_active') }}</label>
    </div>

    <Message v-if="sharedWarning" severity="warn" :closable="false" data-testid="checklist-query-shared">{{ sharedWarning }}</Message>

    <div data-testid="checklist-query-covers">
      <div class="mb-2 flex items-center justify-between gap-2">
        <span class="flex items-center gap-2 text-sm font-medium">
          {{ t('messages.admin.report_qc.checklist.covers_label') }}
          <i
            class="pi pi-info-circle text-gray-400"
            tabindex="0"
            v-tooltip.top="tip(t('messages.admin.report_qc.checklist.covers_tip'))"
            :aria-label="t('messages.admin.report_qc.checklist.covers_tip')"
            data-testid="checklist-query-covers-info"
          />
        </span>
        <Button
          :label="t(pickerOpen ? 'messages.admin.report_qc.checklist.covers_edit_close' : 'messages.admin.report_qc.checklist.covers_edit')"
          size="small"
          text
          class="w-auto!"
          :aria-expanded="pickerOpen"
          v-tooltip.top="tip(t('messages.admin.report_qc.checklist.covers_hint'))"
          data-testid="checklist-query-covers-toggle"
          @click="pickerOpen = !pickerOpen"
        />
      </div>
      <ul class="flex flex-wrap gap-2" data-testid="checklist-query-chips">
        <li v-for="number in query.covers_items" :key="number">
          <Tag severity="secondary" class="font-normal" :data-testid="`checklist-query-chip-${number}`">
            <span class="font-mono font-semibold">{{ number }}</span>
            <span>{{ itemByNumber(number)?.title }}</span>
          </Tag>
        </li>
      </ul>
      <div v-if="pickerOpen" class="mt-3">
        <div v-for="block in blocks" :key="block.key" class="mb-3">
          <span class="mb-1 block text-xs font-medium text-gray-600 uppercase">{{ block.label }}</span>
          <ul class="flex flex-col gap-1">
            <li v-for="item in block.items" :key="item.number" class="flex items-center gap-2" :class="{ 'opacity-55': !item.active }">
              <Checkbox
                :model-value="covers(item.number)"
                binary
                :input-id="inputId(`item-${item.number}`)"
                :data-testid="`checklist-query-item-${item.number}`"
                @update:model-value="checked => setCovers(item.number, checked)"
              />
              <label :for="inputId(`item-${item.number}`)" class="text-sm">{{ item.number }}. {{ item.title }}</label>
            </li>
          </ul>
        </div>
      </div>
      <p class="mt-1 text-sm text-red-600" :class="{ invisible: !errorOf('covers_items') }" data-testid="checklist-query-covers-error">{{ errorOf('covers_items') || '&nbsp;' }}</p>
    </div>

    <div>
      <div class="flex items-center gap-3">
        <ToggleSwitch
          v-model="query.uses_standards_index"
          :disabled="practiceOnly"
          :input-id="inputId('index')"
          data-testid="checklist-query-index"
        />
        <label :for="inputId('index')" class="text-sm font-medium">{{ t('messages.admin.report_qc.checklist.index') }}</label>
        <i class="pi pi-info-circle text-gray-400" v-tooltip.top="tip(t('messages.admin.report_qc.checklist.t1'))" data-testid="checklist-query-index-info" />
      </div>
      <p v-if="practiceOnly" class="mt-1 flex items-start gap-2 text-sm text-gray-600" data-testid="checklist-query-index-guard">
        <i class="pi pi-lock mt-0.5 text-gray-400" aria-hidden="true" />
        <span>{{ t('messages.admin.report_qc.checklist.index_guard') }}<template v-if="switchedOff"> {{ t('messages.admin.report_qc.checklist.index_switched_off') }}</template></span>
      </p>
      <p v-else-if="query.covers_items.length" class="mt-1 text-xs text-gray-500" data-testid="checklist-query-index-allowed">{{ t('messages.admin.report_qc.checklist.index_allowed') }}</p>
      <p v-if="errorOf('uses_standards_index')" class="mt-1 text-sm text-red-600">{{ errorOf('uses_standards_index') }}</p>
    </div>

    <div>
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium">{{ t('messages.admin.report_qc.checklist.prompt_label') }}</span>
        <Button
          v-if="!editingText"
          :label="t('messages.admin.report_qc.checklist.edit_text')"
          icon="pi pi-pencil"
          size="small"
          text
          class="w-auto!"
          data-testid="checklist-query-edit-text"
          @click="editingText = true"
        />
      </div>
      <p class="mb-2 text-xs text-gray-500">{{ t('messages.admin.report_qc.checklist.prompt_two_texts') }}</p>
      <template v-if="editingText">
        <Textarea v-model="query.prompt" rows="8" auto-resize class="font-mono text-sm" :invalid="!!errorOf('prompt')" data-testid="checklist-query-prompt" />
        <p class="mt-1 text-xs text-gray-500">{{ t('messages.admin.report_qc.checklist.prompt_hint') }}</p>
      </template>
      <pre v-else class="rounded-md border border-gray-200 px-3 py-2 font-mono text-sm whitespace-pre-wrap text-gray-800" data-testid="checklist-query-prompt-read">{{ query.prompt }}</pre>
      <p class="mt-1 text-sm text-red-600" :class="{ invisible: !errorOf('prompt') }" data-testid="checklist-query-prompt-error">{{ errorOf('prompt') || '&nbsp;' }}</p>

      <div v-if="suffix" class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-testid="checklist-query-suffix">
        <span class="mb-1 flex items-center gap-2 text-xs font-medium">
          <i class="pi pi-lock text-gray-400" aria-hidden="true" />{{ t('messages.admin.report_qc.checklist.suffix_label') }}
        </span>
        <span class="font-mono">{{ suffix }}</span>
      </div>
    </div>
  </section>
</template>
